import React, { useState } from "react";

import { useIntl } from "react-intl";

import PageMeta from "Components/PageMeta";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import {
  selectFeatureEditPlanEnabled,
  selectFeatureUsageBasedBillingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import {
  deleteSubscription,
  loadingSelector
} from "Reducers/organization/subscription";
import { gitProjectSelector, updateProject } from "Reducers/project/project";
import { subscriptionSelector } from "Reducers/subscription";
import DeleteSubscriptionModal from "src/organization/pages/billing/plan/details/components/deleteModal/DeleteSubscriptionModal";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import EditPlanButton from "../EditPlanButton";

import * as S from "./ProjectGeneralSettings.styles";
import ProjectGeneralSettingsForm from "./ProjectGeneralSettingsForm";

import type { GeneralProjectFormDataType } from "src/organization/pages/project/settings/ProjectGeneralSettings/type";

const ProjectGeneralSettings = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const usageBasedPagesEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );

  const editPlanEnabled = useAppSelector(selectFeatureEditPlanEnabled);

  const { organizationId, projectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const subscription = useSelectorWithUrlParams(subscriptionSelector, {
    id: project?.getSubscriptionId()
  });

  const canDelete = subscription?.hasLink("delete");
  const isLoadingSubscription = useAppSelector(loadingSelector, () => false);

  const onSave = (data: GeneralProjectFormDataType) => {
    dispatch(
      updateProject({
        organizationDescriptionId: organizationId,
        projectDescriptionId: projectId,
        shouldPreferUpdateThroughSubscription: true,
        data
      })
    );
  };

  if (!project?.id) {
    return null;
  }
  return (
    <S.Wrapper>
      <S.SectionHeader>
        <PageMeta title={`Settings | ${project?.title}`} />
        <S.Title tag="h2" id="settings-heading">
          {intl.formatMessage({
            id: "general_project_settings",
            defaultMessage: "General Project Settings"
          })}
        </S.Title>
      </S.SectionHeader>
      <S.ButtonReposition>
        {editPlanEnabled && !usageBasedPagesEnabled && (
          <EditPlanButton subscription={subscription} />
        )}
      </S.ButtonReposition>

      <section aria-labelledby="settings-heading">
        <ProjectGeneralSettingsForm project={project} onSave={onSave} />
        {canDelete && !isLoadingSubscription && usageBasedPagesEnabled && (
          <S.DeleteProjectButton
            onClick={() => {
              setOpenDeleteModal(true);
            }}
            analyticId="settings.plan.project.delete.button"
            variant="critical"
          >
            {intl.formatMessage({
              id: "settings.plan.project.delete.button",
              defaultMessage: "Delete project"
            })}
          </S.DeleteProjectButton>
        )}
      </section>

      {subscription && (
        <DeleteSubscriptionModal
          isOpen={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          subscription={subscription}
          onDelete={() =>
            dispatch(
              deleteSubscription({
                subscription,
                organizationId,
                settings: { redirect: `/${organizationId}` }
              })
            )
          }
        />
      )}
    </S.Wrapper>
  );
};

export default ProjectGeneralSettings;
